import { createContext, FunctionComponent } from 'preact';
import { useCallback, useContext, useMemo, useState } from 'preact/hooks';
import type { ErrorParam } from '@schibsted-svp/web-player';

import { usePlayerEvent } from './usePlayerEvent';
import { useJWPlayerEvent } from './useJWPlayerEvent';

export interface ErrorDetails {
  code?: number;
  message: string;
  referenceId?: string;
  sourceError?: object | null;
}

interface PlayerErrorContextProps {
  errorDetails?: ErrorDetails;
}

const PlayerErrorContext = createContext<PlayerErrorContextProps>({});

interface PlayerErrorProviderProps {
  initialError?: ErrorDetails;
}

const SVP_ERROR_CODES = [
  12000, 12001, 14001, 14002, 14005, 14003, 14004, 15000, 15101, 15102, 15103,
];

const isSvpError = (error: ErrorParam) => {
  return error.code && SVP_ERROR_CODES.includes(error.code);
};

export const PlayerErrorProvider: FunctionComponent<
  PlayerErrorProviderProps
> = ({ initialError, children }) => {
  const [errorDetails, setErrorDetails] = useState(initialError);

  const handleError = useCallback((error: ErrorParam) => {
    if (isSvpError(error)) {
      setErrorDetails({
        code: error.code,
        referenceId: error.referenceId,
        message: error.details || error.message,
      });
    }
  }, []);

  const handleJWError = useCallback((error: jwplayer.ErrorParam) => {
    setErrorDetails({
      code: error.code,
      message: error.message,
      sourceError: error.sourceError,
    });
  }, []);

  const handleNewAsset = useCallback(() => {
    setErrorDetails(undefined);
  }, []);

  useJWPlayerEvent('error', handleJWError);
  usePlayerEvent('error', handleError);
  usePlayerEvent('assetReady', handleNewAsset);
  usePlayerEvent('playlistItem', handleNewAsset);
  usePlayerEvent('recovered', handleNewAsset);

  const state = useMemo(() => {
    return {
      errorDetails,
    };
  }, [errorDetails]);

  return (
    <PlayerErrorContext.Provider value={state}>
      {children}
    </PlayerErrorContext.Provider>
  );
};

export const usePlayerError = () => {
  const playerError = useContext(PlayerErrorContext);

  if (process.env.NODE_ENV !== 'production' && !playerError) {
    console.warn(
      'Player error context is undefined, please verify you are calling usePlayerError() as child of a <PlayerErrorProvider> component.'
    );
  }

  return playerError;
};
